import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatFileSize } from '../../../components/attachments/fileIconUtils';
import { cn } from '../../../lib/utils';
import type { SystemHealthSnapshot } from '../types';
import SectionCard from './SectionCard';
import DetailRow from './DetailRow';
import { zoneStatTileClass, zoneStatTileLabelClass } from '../theme';

interface ApplicationDataCardProps {
  data: SystemHealthSnapshot;
}

const quickLinks = [
  { label: 'Email accounts', path: '/email-accounts' },
  { label: 'Sync logs', path: '/admin/email-logs' },
  { label: 'File storage', path: '/admin/settings/files-storage' },
  { label: 'Activity logs', path: '/activity-logs' },
];

const ApplicationDataCard: React.FC<ApplicationDataCardProps> = ({ data }) => {
  const { colors } = useTheme();
  const uploads = data.disk.uploads;

  return (
    <SectionCard zone="infrastructure" title="Application & data" subtitle="Counts and storage used by ucomm">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Active users', value: data.application.activeUsers.toLocaleString() },
          { label: 'Sync-enabled accounts', value: data.application.syncEnabledAccounts.toLocaleString() },
          { label: 'Cached emails', value: data.database.emailCount?.toLocaleString() ?? '—' },
          {
            label: 'Uploads on disk',
            value:
              uploads.dirSizePending && uploads.dirSizeBytes == null
                ? '…'
                : uploads.dirSizeBytes != null
                  ? formatFileSize(uploads.dirSizeBytes)
                  : '—',
          },
        ].map((tile) => (
          <div key={tile.label} className={zoneStatTileClass('infrastructure')}>
            <p className={zoneStatTileLabelClass('infrastructure')}>{tile.label}</p>
            <p className={cn('text-lg font-bold tabular-nums mt-0.5', colors.text)}>{tile.value}</p>
          </div>
        ))}
      </div>

      <DetailRow label="Database size" value={data.database.databaseSizeBytes != null ? formatFileSize(data.database.databaseSizeBytes) : '—'} />
      <DetailRow label="Attachment records" value={data.database.attachmentFileCount?.toLocaleString() ?? '—'} />
      <DetailRow label="Uploads path" value={uploads.path} mono />

      <div className={cn('flex flex-wrap gap-2 mt-4 pt-4 border-t', colors.border)}>
        {quickLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className={cn(
              'text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors',
              'border-slate-200/80 bg-slate-50/60 dark:border-slate-700/50 dark:bg-slate-900/30',
              colors.textSecondary,
              colors.interactiveHover,
            )}
          >
            {link.label}
          </Link>
        ))}
      </div>
    </SectionCard>
  );
};

export default ApplicationDataCard;
